import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../ProjectContext';
import { getProjectId, getProjectName } from '@shared/targetHelpers';
import { askChatWithData, exportChatAnswer, saveChatAnswerToProject } from '../services/api';
import { formatAnswer, parseInline } from '@shared/formatAnswer';
import { citationLabel } from '@shared/citationLabel';
import { Button, Loading, ErrorText, errorMessage } from '../components/ui';

// A citation marker is "[3]" or "[1, 2]", so one marker can name several
// sources - all of them are made individually clickable rather than the
// bracket as a whole.
function Cite({ value, onJump }) {
  const numbers = String(value).replace(/[[\]]/g, '').split(',').map((n) => Number(n.trim())).filter(Number.isFinite);
  if (!numbers.length || !onJump) return <span className="cite">{value}</span>;
  return (
    <span className="cite">
      [{numbers.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 ? ', ' : ''}
          <button type="button" className="cite-link" onClick={() => onJump(n)} title={`Go to source ${n}`}>{n}</button>
        </React.Fragment>
      ))}]
    </span>
  );
}

function Inline({ text, onJump }) {
  return parseInline(text).map((part, i) => {
    if (part.type === 'bold') return <strong key={i}>{part.value}</strong>;
    if (part.type === 'code') return <code key={i}>{part.value}</code>;
    if (part.type === 'cite') return <Cite key={i} value={part.value} onJump={onJump} />;
    return <React.Fragment key={i}>{part.value}</React.Fragment>;
  });
}

// What each [n] in the answer refers to. Without this the markers are
// decoration: the reader sees "[3]" and has no way to learn what source 3 is,
// which is the opposite of what a citation is for.
// One row, shared by the cited list and the retrieved-but-uncited one below
// it, so a source reads the same either way - the only difference between
// them is whether the answer used it.
function SourceRow({ s, projectId, highlight = 0, registerRef }) {
  const n = s.sourceNo;
  const where = [s.uploadType, s.packageName, s.block, s.level, s.location].filter(Boolean).join(' · ');
  // "Page 7" or "Pages 150-160" - the first number is where the passage
  // starts, which is where a reader wants to land.
  //
  // Anchored to the "Page"/"Pages" prefix rather than taking any digit:
  // a chunk with no page range falls back to a label like "analysis part
  // 3", and a loose match would read that as page 3 and open the
  // document at a page the passage has nothing to do with.
  const firstPage = Number.parseInt(String(s.pageRange || '').match(/^Pages?\s+(\d+)/i)?.[1] || '', 10);
  // The document page is keyed by blobName, so a source that carries one
  // can be opened; an agent result with no underlying file cannot, and
  // is shown as plain text rather than as a link that goes nowhere.
  // from=chat so the document can offer a way back to the conversation
  // rather than only to the documents list - arriving from a citation
  // and arriving from the list want different exits.
  const params = new URLSearchParams({ from: 'chat' });
  if (Number.isFinite(firstPage)) params.set('page', String(firstPage));
  const href = s.blobName && projectId
    ? `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(s.blobName)}?${params}`
    : '';
  return (
    <div
      ref={registerRef ? (el) => registerRef(n, el) : undefined}
      className={`answer-source${highlight === n ? ' is-target' : ''}`}
    >
      <span className="answer-source-no">{n}</span>
      <div className="answer-source-body">
        <div className="answer-source-title">
          {/* Same tab, deliberately. A new tab starts with empty
              sessionStorage, which is where MSAL caches its tokens - so
              it opened with no session and demanded a fresh sign-in,
              then landed on the home page rather than the document.
              Navigating here keeps the session, and the thread now
              survives the trip, so Back returns to the answer intact. */}
          {/* The same label the downloaded and signed documents use, so
              a source reads identically in the conversation and in the
              record built from it. */}
          {href
            ? <Link to={href}>{citationLabel(s)}</Link>
            : citationLabel(s)}
        </div>
        {where || s.pageRange
          ? <div className="answer-source-where">{[s.pageRange, where].filter(Boolean).join(' · ')}</div>
          : null}
        {s.excerpt ? <div className="answer-source-excerpt">{s.excerpt}</div> : null}
      </div>
    </div>
  );
}

function SourceList({ sources, projectId, highlight, registerRef }) {
  if (!sources.length) return null;
  return (
    <div className="answer-sources">
      <div className="answer-sources-head">Sources</div>
      {sources.map((s) => (
        <SourceRow
          key={s.sourceNo}
          s={s}
          projectId={projectId}
          highlight={highlight}
          registerRef={registerRef}
        />
      ))}
    </div>
  );
}

// Structural checks the API ran over the answer's own tables.
//
// Shown, never enforced: nothing here changed the answer, and a check firing
// does not mean a figure is wrong - it means the table is arranged in a way
// that has misled a reader before. An "error" is a malformed or
// self-contradictory table; "to review" is a pattern that is often fine.
//
// Silent when a table is clean, so the band appearing means something. And
// deliberately not hidden behind the same collapse as the uncited sources:
// whoever is about to sign a record should not have to go looking.
function TableChecks({ checks }) {
  if (!checks || !checks.tables || !checks.findings?.length) return null;
  const errors = checks.findings.filter((f) => f.severity === 'error');
  const review = checks.findings.filter((f) => f.severity !== 'error');
  return (
    <details className={`answer-checks${errors.length ? ' has-errors' : ''}`} open={errors.length > 0}>
      <summary>
        {errors.length
          ? `${errors.length} structural ${errors.length === 1 ? 'problem' : 'problems'} in this table`
          : `${review.length} thing${review.length === 1 ? '' : 's'} to review in this table`}
      </summary>
      {[...errors, ...review].map((f, i) => (
        <div key={`${f.check}-${i}`} className="answer-check">
          <span className={`answer-check-tag${f.severity === 'error' ? ' is-error' : ''}`}>
            {f.severity === 'error' ? 'problem' : 'review'}
          </span>
          <span>{f.detail}</span>
        </div>
      ))}
    </details>
  );
}

// The definitions pages the answer was read with.
//
// Not sources, and shown apart from them for that reason: these say how to
// read a record - what a classification letter covers, what "in general
// accordance with" claims, whether a configuration is the thing being asked
// about - and they are never the authority for a value.
//
// They earned a place on screen by changing answers. Three things that had
// survived nine prompt rules were fixed the run after a definitions page went
// in, which makes "which vocabulary was in force" worth being able to see -
// and worth noticing when it changes.
//
// Absent from the exported record on purpose: that document lists what the
// answer relied on as evidence, and printing a glossary beside the sources
// would invite the reading these pages exist to prevent.
function DefinitionsUsed({ items }) {
  if (!items?.length) return null;
  return (
    <div className="answer-definitions">
      <span className="answer-definitions-head">Read with</span>
      {items.map((d) => (
        <span key={d.fileName} className="answer-definitions-file">
          {d.fileName}{d.truncated ? ' (truncated)' : ''}
        </span>
      ))}
    </div>
  );
}

// What retrieval found and the answer did not use.
//
// Until now these were discarded, and the cost of that was not visible until
// a comparison table came back with a product's test standard missing. Two
// very different failures look identical from the outside - retrieval never
// found the passage, or it found it and the answer ignored it - and only one
// of them is fixed by changing retrieval. Showing them tells the two apart
// without having to read the index.
//
// Collapsed, because on a good answer this is noise, and kept out of the
// exported record entirely: a document that cites its sources should list
// what it relied on, not everything that was considered.
function UnusedSources({ items, projectId }) {
  if (!items.length) return null;
  return (
    <details className="answer-sources answer-sources-unused">
      <summary>
        {items.length} more retrieved, not cited
      </summary>
      {items.map((s) => <SourceRow key={s.sourceNo} s={s} projectId={projectId} />)}
    </details>
  );
}

// Puts the table on the clipboard tab-separated, which is the format Excel and
// Google Sheets parse into columns on paste - so a comparison lands as a real
// grid rather than as one string per row.
//
// Deliberately NOT CSV: these answers are full of part numbers like 2395767
// and references like WHI-09/02-22-000001-03, and Excel's CSV import mangles
// exactly those - dropping leading zeros and reading some as dates. Pasted
// TSV goes in as typed.
function CopyTableButton({ table }) {
  const [state, setState] = useState('');

  async function copy() {
    // formatAnswer already builds this tab-separated fallback for renderers
    // that cannot draw a table; rebuilding it here would be a second copy to
    // keep in step.
    const tsv = table.text || [table.header, ...table.rows].map((r) => r.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      setState('copied');
    } catch {
      // Blocked outside a secure context. The table is selectable on the page,
      // so say so rather than failing silently.
      setState('failed');
    }
    setTimeout(() => setState(''), 2500);
  }

  return (
    <button type="button" className="table-copy" onClick={copy}>
      {state === 'copied' ? 'Copied — paste into Excel'
        : state === 'failed' ? 'Copy blocked — select it instead'
        : 'Copy table'}
    </button>
  );
}

// The model writes the markdown it naturally reaches for. Rendered as one
// string it arrived as a wall of text with literal asterisks in it.
export function AnswerBody({ text, onJump }) {
  const blocks = formatAnswer(text);
  if (!blocks.length) return <>{text}</>;
  return blocks.map((block, i) => {
    if (block.type === 'h') return <p key={i} className="answer-h"><Inline text={block.text} onJump={onJump} /></p>;
    if (block.type === 'ul') {
      return <ul key={i} className="answer-list">{block.items.map((it, j) => <li key={j}><Inline text={it} onJump={onJump} /></li>)}</ul>;
    }
    if (block.type === 'ol') {
      return <ol key={i} className="answer-list">{block.items.map((it, j) => <li key={j}><Inline text={it} onJump={onJump} /></li>)}</ol>;
    }
    // Wrapped in its own scroller: a comparison across three manufacturers is
    // nine columns wide, and without this the PAGE scrolls sideways on a
    // phone - which breaks every other answer in the thread, not just this one.
    if (block.type === 'table') {
      return (
        <div key={i} className="answer-table-block">
          <CopyTableButton table={block} />
          <div className="answer-table-wrap">
          <table className="answer-table">
            <thead>
              <tr>{block.header.map((cell, j) => <th key={j}><Inline text={cell} onJump={onJump} /></th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, j) => (
                <tr key={j}>
                  {/* Indexed against the HEADER, not the row: a model that
                      drops a trailing pipe yields a short row, and mapping the
                      row itself would shift every later cell left, under the
                      wrong column heading. */}
                  {block.header.map((_, k) => <td key={k}><Inline text={row[k] || ''} onJump={onJump} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      );
    }
    return <p key={i} className="answer-p"><Inline text={block.text} onJump={onJump} /></p>;
  });
}

// One answer plus the sources it was built from. The refs live here rather
// than on the page so each answer jumps within itself - with one shared map,
// clicking [2] on the third answer would scroll to the first answer's
// source 2.
// The source numbers the answer actually cites.
//
// Retrieval hands over everything it found; the answer uses what it needed.
// Listing all of it as "Sources" says nine documents contributed when one did,
// and a reader checking the answer has no way to tell which. A citation list
// should hold what was cited.
//
// Numbers are NOT renumbered: the answer says [4], so the entry has to be
// labelled 4 or the two stop corresponding.
function citedNumbers(text) {
  const found = new Set();
  for (const marker of String(text || '').match(/\[\d+(?:\s*,\s*\d+)*\]/g) || []) {
    for (const n of marker.replace(/[[\]]/g, '').split(',')) {
      const value = Number(n.trim());
      if (Number.isFinite(value)) found.add(value);
    }
  }
  return found;
}

// Save the answer as a document.
//
// An answer worth exporting is usually one with a table in it, and a table is
// exactly what a screenshot or a copy-paste destroys. The blocks rendered on
// screen are sent as-is, so the file matches what was read.
//
// Download and save are different acts and say so. The two export buttons
// once both read "Save as ...", which was harmless while they were the only
// ones - beside a button that genuinely saves, three "Save"s would have made
// the one that keeps a record indistinguishable from the two that do not.
function ExportButtons({ question, text, blocks, sources, projectId, projectName, model = '' }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [kept, setKept] = useState('');

  const save = useCallback(async (format) => {
    setBusy(format);
    setError('');
    try {
      // No title sent: the API names the document from the answer's own first
      // heading, which is shorter and more descriptive than the question. The
      // question still travels, and is printed under its own label.
      await exportChatAnswer({ format, question, blocks, sources, projectName });
    } catch (e) {
      // The likeliest cause is an API that has not been deployed with this
      // endpoint yet, so say what happened rather than failing silently.
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  }, [question, blocks, sources, projectName]);

  // Keeping it is a different act from downloading it, so it is a different
  // button. The file goes to Reports, not to Documents: an answer is this
  // app's output, not the customer's source material, and it is never indexed
  // - otherwise a later question could cite an earlier answer as evidence.
  const keep = useCallback(async () => {
    setBusy('keep');
    setError('');
    setKept('');
    try {
      await saveChatAnswerToProject({ format: 'pdf', question, text, blocks, sources, projectId, projectName, model });
      setKept('Saved to Reports');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  }, [question, text, blocks, sources, projectId, projectName, model]);

  if (!blocks.length) return null;

  return (
    <div className="answer-export">
      <button type="button" className="table-copy" disabled={!!busy} onClick={() => save('docx')}>
        {busy === 'docx' ? 'Preparing…' : 'Download Word'}
      </button>
      <button type="button" className="table-copy" disabled={!!busy} onClick={() => save('pdf')}>
        {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
      </button>
      <button type="button" className="table-copy" disabled={!!busy} onClick={keep}>
        {busy === 'keep' ? 'Saving…' : 'Save to project'}
      </button>
      {kept ? <span className="saved">{kept}</span> : null}
      {error ? <span className="answer-export-error">{error}</span> : null}
    </div>
  );
}

function AiMessage({ message, projectId, question, projectName }) {
  const refs = useRef(new Map());
  const [highlight, setHighlight] = useState(0);

  const { sources, unused } = useMemo(() => {
    const all = Array.isArray(message.sources) ? message.sources : [];
    const cited = citedNumbers(message.text);
    // Nothing cited means the model ignored the instruction, or the answer was
    // "I could not find that". Showing everything beats showing nothing, since
    // the reader can then judge the retrieval for themselves.
    if (!cited.size) return { sources: all, unused: [] };
    const used = all.filter((s) => cited.has(Number(s.sourceNo)));
    // A model citing [9] when eight were supplied would otherwise leave the
    // reader with an answer and no sources at all. Showing everything is the
    // lesser failure - they can still check it.
    if (!used.length) return { sources: all, unused: [] };
    return { sources: used, unused: all.filter((s) => !cited.has(Number(s.sourceNo))) };
  }, [message.sources, message.text]);

  // Parsed once here and shared with the exporter, so the document and the
  // screen can never disagree about what the answer contained.
  const blocks = useMemo(() => formatAnswer(message.text), [message.text]);

  const registerRef = useCallback((n, el) => {
    if (el) refs.current.set(n, el);
    else refs.current.delete(n);
  }, []);

  const jump = useCallback((n) => {
    const el = refs.current.get(n);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // The scroll is easy to miss on a long page and may not move at all on a
    // short one, so the highlight is what actually confirms which source the
    // marker meant.
    setHighlight(n);
    setTimeout(() => setHighlight((cur) => (cur === n ? 0 : cur)), 2000);
  }, []);

  return (
    <div className="bubble ai">
      <AnswerBody text={message.text} onJump={jump} />
      <SourceList
        sources={sources}
        projectId={projectId}
        highlight={highlight}
        registerRef={registerRef}
      />
      <TableChecks checks={message.tableChecks} />
      <DefinitionsUsed items={message.definitions} />
      <UnusedSources items={unused} projectId={projectId} />
      <ExportButtons question={question} text={message.text} blocks={blocks} sources={sources} projectId={projectId} projectName={projectName} model={message.model} />
    </div>
  );
}

// The thread survives leaving the tab and reloading the page, and is dropped
// when the browser tab closes - sessionStorage, which is also where this app
// keeps its sign-in, so the two expire together.
//
// Keyed by project, or switching projects would show the previous project's
// conversation against the wrong documents - and its citations would link to
// files that are not in the project on screen.
const CHAT_KEY_PREFIX = 'ydyai.chat.';
// Answers carry their sources, so a long thread is not small. Keeping the
// last few bounds what is written without touching what is displayed: a
// reload shows recent history rather than everything.
const CHAT_PERSIST_MESSAGES = 12;

function chatKey(projectId) {
  return `${CHAT_KEY_PREFIX}${projectId || 'none'}`;
}

function loadThread(projectId) {
  try {
    const raw = sessionStorage.getItem(chatKey(projectId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Private windows and blocked site data both throw here. A chat that
    // cannot be remembered still has to work.
    return [];
  }
}

function saveThread(projectId, messages) {
  try {
    const trimmed = messages.slice(-CHAT_PERSIST_MESSAGES);
    sessionStorage.setItem(chatKey(projectId), JSON.stringify(trimmed));
  } catch {
    // Quota exceeded, or storage unavailable. Losing the saved copy is not
    // worth interrupting the conversation for.
  }
}

function clearThread(projectId) {
  try { sessionStorage.removeItem(chatKey(projectId)); } catch { /* see above */ }
}

// All retrieval and reasoning happens in the backend (chatWithData searches
// the Azure AI Search index and calls the model); this page only carries the
// question and renders the answer.
export default function ChatPage() {
  const { project } = useProject();
  const projectId = getProjectId(project);
  // Read during the initial state, not in an effect: an effect would render
  // an empty thread first and then replace it, which reads as the history
  // being lost and coming back.
  const [messages, setMessages] = useState(() => loadThread(projectId));
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Whether to widen retrieval past this project to reference material. Held
  // across questions in a session, so somebody working through a compliance
  // check is not re-ticking it every time, and not persisted - it is a mode
  // you are in for a while, not a setting.
  const [includeReferences, setIncludeReferences] = useState(false);

  // Switching project inside the same mount has to swap threads too - the
  // page does not remount when the project changes underneath it.
  const loadedFor = useRef(projectId);
  useEffect(() => {
    if (loadedFor.current === projectId) return;
    loadedFor.current = projectId;
    setMessages(loadThread(projectId));
    setError('');
  }, [projectId]);

  useEffect(() => {
    if (messages.length) saveThread(projectId, messages);
  }, [projectId, messages]);

  // Each question is sent with no history, so a long thread is only ever
  // clutter on screen - clearing it is how you start a fresh line of enquiry.
  // This is the only thing that ends a thread now, so it has to clear the
  // stored copy too or the old conversation comes back on the next reload.
  function reset() {
    setMessages([]);
    setQuestion('');
    setError('');
    clearThread(projectId);
  }

  async function ask() {
    const q = question.trim();
    if (!q) return;
    setMessages((prev) => [...prev, { role: 'me', text: q }]);
    setQuestion('');
    setBusy(true);
    setError('');
    try {
      const res = await askChatWithData({
        question: q,
        projectId,
        projectName: getProjectName(project),
        includeReferences,
      });
      // The API has always returned a full record per source - title, file
      // name, blobName, an excerpt - and this kept only `answer` and dropped
      // the rest. The answer cites [1], [2] against a list that was never
      // shown, so every marker pointed at nothing.
      setMessages((prev) => [...prev, {
        role: 'ai',
        text: res?.answer || res?.response || 'No answer returned.',
        sources: Array.isArray(res?.sources) ? res.sources : [],
        // Which model answered. Kept on the message so that saving it records
        // what produced it - a record that says what it concluded and not what
        // concluded it is half a provenance statement, and the verification
        // that gets signed later copies this in.
        model: res?.model || '',
        // Structural checks the API ran on the answer's own tables. Carried on
        // the message so they sit with the answer they describe rather than
        // with whatever question is in the box by the time they are read.
        tableChecks: res?.tableChecks || null,
        // Names only - the definitions text is not returned and is not needed.
        definitions: Array.isArray(res?.definitions) ? res.definitions : [],
      }]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <p className="sub" style={{ margin: 0 }}>Ask about anything uploaded to this project — drawings, photos, reports and their AI analysis.</p>
        {messages.length > 0 && (
          <Button compact secondary onClick={reset} disabled={busy}>New chat</Button>
        )}
      </div>
      <div className="chat-log">
        {messages.map((m, i) => (
          m.role === 'ai'
            ? (
              <AiMessage
                key={i}
                message={m}
                projectId={projectId}
                // The question this answered is the message before it, which is
                // what names the exported file - "Chat answer (3).docx" tells a
                // reader nothing a month later.
                question={messages[i - 1]?.role === 'me' ? messages[i - 1].text : ''}
                projectName={getProjectName(project)}
              />
            )
            : <div key={i} className="bubble me">{m.text}</div>
        ))}
        {busy && <div className="bubble ai"><Loading text="Thinking…" /></div>}
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="row">
        <input
          className="input"
          style={{ flex: 1, minWidth: 200 }}
          value={question}
          placeholder="e.g. Which drawings changed on Level 1?"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()}
        />
        <Button compact onClick={ask} disabled={!question.trim() || busy}>Ask</Button>
      </div>
      {/* Under the box rather than beside it, because it is a property of the
          question being asked and not of the project.

          Off by default. Most questions are about this project's own
          documents, and quietly checking every answer against a shelf of
          standards would change what comes back without anyone asking for it -
          and put reference text in answers that have nothing to do with it. */}
      <label className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={includeReferences}
          onChange={(e) => setIncludeReferences(e.target.checked)}
        />
        <span className="card-hint" style={{ margin: 0 }}>
          Include reference standards
        </span>
      </label>
    </>
  );
}
